import React, { useState, useMemo, useEffect } from "react";
import useDataSourceManager from "hooks/useDataSourceManager";
import { v4 as uuidv4 } from "uuid";
import { useTheme } from "@mui/material/styles";
import mainStyle from "../../../mainStyle.module.scss";
import { StyledSelect } from "components/Select/searchWithSelect";
import { MenuSelect } from "components/Select/menuSelect";
import { ErrorNotification } from "pages/System/UserManagement/ErrorHandle";
import {
  Box,
  Button,
  Typography,
  Divider,
  TextField,
  CircularProgress,
} from "@mui/material";

import { SolidButton } from "components/buttons/SolidButton";
import { OutlineButton } from "components/buttons/OutlineButton";
import { OutlineRectangleButton } from "components/buttons/OutlineRectangleButton";
import ModalContainer from "components/ModalContainer";

import { ReactComponent as UnverifiedIcon } from "assets/images/icons/icn_table_cell_Unverified.svg";
import { ReactComponent as AddIcon } from "assets/images/icons/icn_table_cell_Add.svg";
import { ReactComponent as AddHoverIcon } from "assets/images/icons/icn_table_cell_Add_hover.svg";
import { ReactComponent as CloseIcon } from "assets/images/icons/icn_close.svg";

const InviteUserModal = ({
  openInviteUserModal,
  setOpenInviteUserModal,
  allOrgNames,
  orgRoleHierarchy,
  mspRoleHierarchy,
  entId,
  currentUserMspRoleId,
  getListEnterpriseUsers,
  getListPendingEnterpriseUsers,
}) => {
  const theme = useTheme();

  const [isAddHovered, setIsAddHovered] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);
  const [createUuid, setCreateUuid] = useState(uuidv4());
  const [errorMessageHandleModal, setErrorMessageHandleModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [actionModal, setActionModal] = useState(false);
  const { dataMap, getData, decodeTokenData, sendRequest, sendBatch } =
    useDataSourceManager();
  const initialData = [
    {
      email: "",
      mspRoleId: "",
      mspRoleName: "",
      orgAccesses: [],
    },
  ];

  const [data, setData] = useState(initialData);

  const roleNameToIdMap = useMemo(() => {
    const map = new Map();
    orgRoleHierarchy.forEach((r) => map.set(r.orgRoleName, r.orgRoleId));
    return map;
  }, [orgRoleHierarchy]);

  const isValidInputStyles = (isValid) => ({
    width: "66%",
    "& .MuiOutlinedInput-root": {
      borderRadius: theme.shape.customBorderRadius[1],
      height: "31px",
      backgroundColor: theme.palette.primary.transparent,

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.borderGreyPrimary
          : theme.palette.primary.redPrimary,
      },

      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.mainHover
          : theme.palette.primary.redPrimary,
      },

      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderColor: isValid
          ? theme.palette.primary.main
          : theme.palette.primary.redPrimary,
      },
    },
  });

  const handleCloseModal = () => {
    setOpenInviteUserModal(false);
  };

  const isValidEmail = (email) => {
    return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(
      email.trim(),
    );
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setData((prev) => {
      const updated = [...prev];
      updated[0].email = value;
      return updated;
    });
    setIsEmailValid(isValidEmail(value));
  };

  const handleChange = (index, field, value) => {
    setData((prevData) => {
      const updated = [...prevData];
      const user = { ...updated[0] };

      if (field === "mspRole") {
        user.mspRoleName = value.orgRoleName;
        user.mspRoleId = value.orgRoleId;

        if (
          value?.orgRoleName === "Admin" ||
          value?.orgRoleName === "Super Admin"
        ) {
          user.orgAccesses = [];
        } else if (
          value?.orgRoleName === "Viewer" &&
          (!user.orgAccesses || user.orgAccesses.length === 0)
        ) {
          user.orgAccesses = [{ orgName: "", orgRoleName: "" }];
        }
      } else if (field === "org") {
        const orgAccesses = [...user.orgAccesses];
        orgAccesses[index] = {
          ...orgAccesses[index],
          orgId: value.orgId,
          orgName: value.orgName,
          orgRoleId: value.orgRoleId,
          orgRoleName: value.orgRoleName,
        };
        user.orgAccesses = orgAccesses;
      } else if (field === "role") {
        const orgAccesses = [...user.orgAccesses];
        orgAccesses[index] = {
          ...orgAccesses[index],
          orgRoleId: value.orgRoleId,
          orgRoleName: value.orgRoleName,
        };
        user.orgAccesses = orgAccesses;
      }

      updated[0] = user;
      return updated;
    });
  };

  const handleAddOrgAccess = () => {
    setData((prev) => {
      const updated = [...prev];
      updated[0].orgAccesses.push({
        orgId: "",
        orgName: "",
        orgRoleName: "",
        orgRoleId: null,
      });
      return updated;
    });
  };

  const handleDeleteOrgAccess = (index) => {
    setData((prev) => {
      const updated = [...prev];
      updated[0].orgAccesses.splice(index, 1);
      return updated;
    });
  };

  const isSaveDisabled = data.some((entry) => {
    if (!entry.email || !isValidEmail(entry.email) || !entry.mspRoleId) {
      return true;
    }

    if (entry.orgAccesses.length === 0 && entry.mspRoleId === 3) {
      return true;
    }

    return entry.orgAccesses.some(
      (access) => !access.orgId || !access.orgRoleId,
    );
  });

  // create new user
  const handleInviteUser = () => {
    const { email, mspRoleId, orgAccesses } = data[0];
    setIsLoading(true);
    sendRequest(
      "/ents/:entId/invitations.create",
      createUuid,
      { entId },
      { email: email, mspRoleId: mspRoleId, orgAccesses: orgAccesses },
    );
  };

  useEffect(() => {
    const createUsersResponse = getData(createUuid);
    if (!createUsersResponse) return;

    setIsLoading(false);

    if (createUsersResponse?.code === 500) {
      setErrorMessageHandleModal(true);
      setErrorMessage(createUsersResponse?.message);
      getListEnterpriseUsers();
      /* getListPendingEnterpriseUsers(); */
      setOpenInviteUserModal(false);
    } else if (createUsersResponse?.code === 400) {
      setActionModal(true);
      setErrorMessage(createUsersResponse?.message);
    } else {
      getListEnterpriseUsers();
      /* getListPendingEnterpriseUsers(); */
      setOpenInviteUserModal(false);
    }
  }, [dataMap]);

  useEffect(() => {
    if (openInviteUserModal) {
      setData([
        {
          email: "",
          mspRoleId: "",
          mspRoleName: "",
          orgAccesses: [],
        },
      ]);
      setIsEmailValid(true);
      setErrorMessageHandleModal(false);
      setErrorMessage("");
    }
  }, [openInviteUserModal]);

  return (
    <>
      <ModalContainer display={openInviteUserModal} modalWidth="auto">
        {isLoading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255,255,255,0.6)",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              zIndex: 9999,
            }}
          >
            <CircularProgress sx={{ color: theme.palette.primary.mainHover }} />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              mb: "30px",
              ml: "20px",
              color: theme.palette.primary.main,
              fontFamily: theme.typography.fontFamily,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
            }}
          >
            Invite User
          </Typography>
          <Button
            onClick={handleCloseModal}
            sx={{
              marginRight: "-20px",
              marginTop: "-10px",
              backgroundColor: theme.palette.primary.transparent,
              boxShadow: "none",
              "&:hover": {
                backgroundColor: theme.palette.primary.transparent,
              },
            }}
          >
            <CloseIcon />
          </Button>
        </Box>

        <Box sx={{ display: "flex" }}>
          {/* Profile */}
          <Box sx={{ flex: 1, mb: 12 }}>
            <Box sx={{ marginLeft: "20px" }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography
                    className={mainStyle["user-management-modal-typography"]}
                  >
                    Email
                    <Typography
                      component="span"
                      sx={{ color: theme.palette.primary.redPrimary }}
                    >
                      *
                    </Typography>
                  </Typography>
                  <TextField
                    type="text"
                    value={data[0].email}
                    onChange={handleEmailChange}
                    placeholder="Enter email"
                    sx={isValidInputStyles(isEmailValid)}
                    error={!isEmailValid}
                  />
                </Box>

                {!isEmailValid && (
                  <Typography
                    sx={{
                      color: theme.palette.primary.redPrimary,
                      fontSize: "0.75rem",
                      marginLeft: "9rem",
                      marginTop: "-1rem",
                    }}
                  >
                    Invalid email address
                  </Typography>
                )}

                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography
                    className={mainStyle["user-management-modal-typography"]}
                  >
                    MSP Role
                  </Typography>
                  <MenuSelect
                    value={data[0].mspRoleName}
                    onChange={(opt) => handleChange(null, "mspRole", opt)}
                    options={(() => {
                      if (
                        !currentUserMspRoleId ||
                        typeof currentUserMspRoleId !== "number"
                      )
                        return [];

                      return mspRoleHierarchy
                        .filter(
                          (role) => role.mspRoleId >= currentUserMspRoleId,
                        )
                        .map((role) => ({
                          label: role.mspRoleName,
                          value: role.mspRoleName,
                          orgRoleId: role.mspRoleId,
                          orgRoleName: role.mspRoleName,
                        }));
                    })()}
                    width="100%"
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: theme.palette.primary.greyThread,
              borderWidth: 1,
            }}
          />

          {/* Organization Privilege */}
          <Box sx={{ flex: 1 }}>
            <Box sx={{ marginLeft: "20px" }}>
              <Typography
                sx={{
                  color: theme.palette.primary.greyPrimary,
                  fontSize: theme.typography.customFontSize[1],
                  fontWeight: theme.typography.customFontWeight[1],
                  fontFamily: theme.typography.fontFamily,
                }}
              >
                Organization Privilege
              </Typography>

              <Box sx={{ marginTop: "10px", display: "flex", gap: 2 }}>
                <Typography
                  sx={{
                    width: 177,
                    color: theme.palette.primary.greyThird,
                    fontSize: theme.typography.customFontSize[3],
                    fontWeight: theme.typography.customFontWeight[2],
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Organization
                </Typography>
                <Typography
                  sx={{
                    width: 177,
                    color: theme.palette.primary.greyThird,
                    fontSize: theme.typography.customFontSize[3],
                    fontWeight: theme.typography.customFontWeight[2],
                    fontFamily: theme.typography.fontFamily,
                  }}
                >
                  Role
                </Typography>
              </Box>

              <Divider
                sx={{
                  marginTop: "10px",
                  borderColor: theme.palette.primary.greyThread,
                  borderWidth: 1,
                }}
              />

              <Box sx={{ mt: 2 }}>
                {data[0].mspRoleName === "Super Admin" ||
                data[0].mspRoleName === "Admin" ? (
                  <Box
                    sx={{
                      marginTop: "60px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontFamily: theme.typography.fontFamily,
                      fontSize: theme.typography.customFontSize[2],
                      color: theme.palette.primary.greyPrimary,
                    }}
                  >
                    All Organizations
                  </Box>
                ) : (
                  <>
                    {data[0].orgAccesses.map((site, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          mb: 2,
                          width: "100%",
                        }}
                      >
                        {/* Org */}
                        <Box sx={{ flex: 1, mr: 1 }}>
                          <StyledSelect
                            value={site.orgName}
                            onChange={(opt) => handleChange(index, "org", opt)}
                            options={allOrgNames.map((o) => ({
                              ...o,
                              value: o.orgName,
                              label: o.orgId,
                            }))}
                            disabledOptions={data[0].orgAccesses
                              .filter((_, i) => i !== index)
                              .map((access) => access.orgName)}
                            placeholder="Select Org"
                            width="100%"
                          />
                        </Box>

                        {/* Role */}
                        <Box sx={{ flex: 1, mr: index > 0 ? 1 : 0 }}>
                          <MenuSelect
                            value={site.orgRoleName}
                            onChange={(opt) => handleChange(index, "role", opt)}
                            options={(() => {
                              if (!site.orgName) return [];
                              const matchedOrg = allOrgNames.find(
                                (org) => org.orgName === site.orgName,
                              );
                              if (!matchedOrg) return [];
                              const maxRoleId = roleNameToIdMap.get(
                                matchedOrg.orgRoleName,
                              );
                              if (typeof maxRoleId !== "number") return [];
                              return orgRoleHierarchy
                                .filter((role) => role.orgRoleId >= maxRoleId)
                                .map((role) => ({
                                  label: role.orgRoleName,
                                  value: role.orgRoleName,
                                  orgRoleId: role.orgRoleId,
                                  orgRoleName: role.orgRoleName,
                                }));
                            })()}
                            width="100%"
                            placeholder="Select Role"
                          />
                        </Box>

                        {index > 0 && (
                          <Box
                            sx={{
                              width: 40,
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Button
                              onClick={() => handleDeleteOrgAccess(index)}
                              sx={{
                                padding: 0,
                                minWidth: 0,
                                backgroundColor:
                                  theme.palette.primary.transparent,
                                boxShadow: "none",
                              }}
                            >
                              <UnverifiedIcon />
                            </Button>
                          </Box>
                        )}
                      </Box>
                    ))}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-end",
                        mt: 2,
                      }}
                    >
                      <OutlineRectangleButton
                        onClick={handleAddOrgAccess}
                        onMouseEnter={() => setIsAddHovered(true)}
                        onMouseLeave={() => setIsAddHovered(false)}
                        startIcon={
                          isAddHovered ? <AddHoverIcon /> : <AddIcon />
                        }
                        width="54px"
                        height="28px"
                      >
                        Add
                      </OutlineRectangleButton>
                    </Box>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Footer Buttons */}
        <Box sx={{ display: "flex", justifyContent: "center", mt: 5, mb: 2 }}>
          <OutlineButton
            onClick={handleCloseModal}
            width="100px"
            height="32px"
            sx={{ mr: 2 }}
          >
            Cancel
          </OutlineButton>
          <SolidButton
            onClick={handleInviteUser}
            width="100px"
            height="32px"
            disabled={isSaveDisabled}
          >
            Invite
          </SolidButton>
        </Box>
      </ModalContainer>
      <ModalContainer display={actionModal} modalWidth="500px">
        <Box sx={{ textAlign: "center" }}>
          <Typography
            sx={{
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[5],
              fontWeight: theme.typography.customFontWeight[3],
              fontFamily: theme.typography.fontFamily,
            }}
          >
            Action denied
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Typography
              sx={{
                color: theme.palette.primary.black,
                fontSize: theme.typography.customFontSize[3],
                fontFamily: theme.typography.fontFamily,
                marginTop: "20px",
              }}
            >
              {errorMessage}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <SolidButton
              sx={{ marginTop: "30px" }}
              onClick={() => setActionModal(false)}
              width="100px"
              height="32px"
            >
              OK
            </SolidButton>
          </Box>
        </Box>
      </ModalContainer>
      <ErrorNotification
        open={errorMessageHandleModal}
        onClose={() => setErrorMessageHandleModal(false)}
        message={errorMessage}
      />
    </>
  );
};

export default InviteUserModal;
